
import React, { useEffect, useState } from 'react';
import { Scale, ArrowRight, Database, Box, Info, AlertTriangle, GripVertical } from 'lucide-react';
import { PropertyField } from '../../molecules/PropertyField';
import { WorkflowNodeData } from '../../../types';
import { useEditorConfig } from '../../../contexts/EditorContext';
import { DmnDefinition } from '../../../services/mockDmnService';
import { VariableDefinition } from '../../../utils/variableExtraction';

interface DecisionTabProps {
    data: WorkflowNodeData;
    onChange: (key: string, value: any) => void;
    context?: VariableDefinition[];
}

const DroppableInput: React.FC<{
    value: string;
    onChange: (val: string) => void;
    placeholder?: string;
}> = ({ value, onChange, placeholder }) => {
    const [isOver, setIsOver] = useState(false);

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsOver(false);
        const text = e.dataTransfer.getData('text/plain');
        if (!text) return;

        const newValue = value ? `${value} ${text}` : text;
        onChange(newValue);
    };

    return (
        <input 
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onDragOver={(e) => { e.preventDefault(); setIsOver(true); e.dataTransfer.dropEffect = 'copy'; }}
            onDragLeave={() => setIsOver(false)}
            onDrop={handleDrop}
            placeholder={placeholder}
            className={`w-full px-2 py-1.5 text-xs border rounded bg-white dark:bg-slate-900 focus:outline-none font-mono transition-all
                ${isOver 
                    ? 'border-teal-500 ring-2 ring-teal-500/20 bg-teal-50 dark:bg-teal-900/10' 
                    : 'border-slate-300 dark:border-slate-700 focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20'
                }
            `}
        />
    );
};

export const DecisionTab: React.FC<DecisionTabProps> = ({ data, onChange, context = [] }) => {
    const { services } = useEditorConfig();
    const [definitions, setDefinitions] = useState<DmnDefinition[]>([]);
    const [selectedDef, setSelectedDef] = useState<DmnDefinition | null>(null);
    const [loading, setLoading] = useState(false);
    const [showDebug, setShowDebug] = useState(false);

    useEffect(() => {
        if (services.dmn) {
            setLoading(true);
            services.dmn.getDefinitions()
                .then((defs: DmnDefinition[]) => setDefinitions(defs))
                .catch((err: any) => console.error("DMN load error", err))
                .finally(() => setLoading(false));
        }
    }, [services.dmn]);

    useEffect(() => {
        if (data.decisionFile && definitions.length > 0) {
            const def = definitions.find(d => d.id === data.decisionFile);
            if (def) setSelectedDef(def);
        }
    }, [data.decisionFile, definitions]);

    const handleFileChange = (fileId: string) => {
        onChange('decisionFile', fileId);
        // Clear mapping when file changes
        onChange('inputMapping', {});
        
        const def = definitions.find(d => d.id === fileId);
        setSelectedDef(def || null);
        
        // Cache outputs for variable extraction
        if (def) {
            onChange('_outputSchema', def.outputs);
        }
    };

    const handleMappingChange = (inputName: string, variable: string) => {
        const newMapping = { ...data.inputMapping, [inputName]: variable };
        onChange('inputMapping', newMapping);
    };

    // --- Drag & Drop Logic ---
    const handleDragStart = (e: React.DragEvent, variableName: string) => {
        e.dataTransfer.setData('text/plain', `\${${variableName}}`);
        e.dataTransfer.effectAllowed = 'copy';
    };

    // --- Variable Grouping ---
    const uniqueContext = context.filter((v, i, self) => 
        i === self.findIndex((t) => t.name === v.name)
    );
    const systemVars = uniqueContext.filter(v => v.sourceNodeId === 'system');
    const userVars = uniqueContext.filter(v => v.sourceNodeId !== 'system');
    const groupedUserVars = userVars.reduce((acc, v) => {
        const key = v.sourceNodeLabel || v.sourceNodeId;
        if (!acc[key]) acc[key] = [];
        acc[key].push(v);
        return acc;
    }, {} as Record<string, VariableDefinition[]>);

    return (
        <div className="flex h-full flex-col">
            {/* Header Section */}
            <div className="px-6 pt-6 pb-2 shrink-0 space-y-4">
                <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-800 flex gap-3">
                    <Scale className="text-slate-600 dark:text-slate-400 mt-0.5 shrink-0" size={16} />
                    <div className="space-y-1">
                        <p className="text-xs font-bold text-slate-800 dark:text-slate-300">Decision Rules</p>
                        <p className="text-[11px] text-slate-600 dark:text-slate-400 leading-relaxed">
                            Select a DMN file to execute business rules. Map workflow variables to the decision inputs.
                        </p>
                    </div>
                </div>

                <div>
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Rule File</label>
                    <select
                        value={data.decisionFile || ''}
                        onChange={(e) => handleFileChange(e.target.value)}
                        className="w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 focus:outline-none focus:border-teal-500 transition-all"
                        disabled={loading}
                    >
                        <option value="">Select DMN File...</option>
                        {definitions.map(def => (
                            <option key={def.id} value={def.id}>{def.name} ({def.id})</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 min-h-0 flex px-6 pb-6 gap-6 pt-4">
                {/* Main Config (Left) */}
                <div className="flex-1 overflow-y-auto pr-2">
                    {selectedDef ? (
                        <div className="animate-in fade-in slide-in-from-top-2 duration-300 space-y-6">
                            {/* Input Mapping */}
                            <div className="space-y-2">
                                <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider border-b border-slate-100 dark:border-slate-800 pb-2">
                                    Input Mapping
                                </h4>
                                <div className="space-y-3">
                                    {selectedDef.inputs.map(input => (
                                        <div key={input.name} className="grid grid-cols-[1fr_auto_2fr] gap-3 items-center">
                                            <div className="flex flex-col min-w-0">
                                                <span className="text-xs font-mono font-bold text-slate-700 dark:text-slate-300 truncate" title={input.name}>
                                                    {input.name}
                                                </span>
                                                <span className="text-[9px] text-slate-400">{input.type}</span>
                                            </div>
                                            
                                            <ArrowRight size={12} className="text-slate-300"/>
                                            
                                            <DroppableInput 
                                                value={data.inputMapping?.[input.name] || ''}
                                                onChange={(val) => handleMappingChange(input.name, val)}
                                                placeholder="Drag variable or type..."
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Output Preview */}
                            <div className="space-y-2">
                                <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider border-b border-slate-100 dark:border-slate-800 pb-2">
                                    Expected Outputs
                                </h4>
                                <div className="grid grid-cols-2 gap-2">
                                    {selectedDef.outputs.map(output => (
                                        <div key={output.name} className="p-2 bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-900 rounded text-xs">
                                            <div className="font-bold text-emerald-700 dark:text-emerald-400">{output.name}</div>
                                            <div className="text-[9px] text-emerald-600/70 dark:text-emerald-500/70">{output.type}</div>
                                        </div>
                                    ))}
                                </div>
                                
                                <PropertyField 
                                    config={{ 
                                        name: 'resultVariable', 
                                        label: 'Map Result To Object (Optional)', 
                                        type: 'text', 
                                        placeholder: 'decisionResult',
                                        description: 'If set, all outputs will be wrapped in this object.'
                                    }}
                                    value={data.resultVariable}
                                    onChange={onChange}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="h-full flex items-center justify-center text-slate-400 text-xs italic">
                            Select a DMN file to configure mappings.
                        </div>
                    )}
                </div>

                {/* Variable Sidebar (Right) */}
                <div className="w-64 shrink-0 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden flex flex-col">
                    <div className="p-3 border-b border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-950 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Database size={14} className="text-slate-500"/>
                            <span className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase">Variables</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button 
                                onClick={() => setShowDebug(!showDebug)} 
                                className="p-1 text-slate-400 hover:text-slate-600 rounded"
                                title="Toggle Debug Info"
                            >
                                <Info size={12} />
                            </button>
                            <span className="text-[9px] bg-slate-200 dark:bg-slate-800 text-slate-500 px-1.5 rounded-full">{context.length}</span>
                        </div>
                    </div>
                    
                    <div className="flex-1 overflow-y-auto p-2 space-y-4">
                        {context.length === 0 && (
                            <div className="p-4 text-center">
                                <AlertTriangle className="mx-auto text-amber-400 mb-2" size={20} />
                                <div className="text-[10px] text-slate-400 italic mb-2">No variables found.</div>
                            </div>
                        )}

                        {/* User Variables */}
                        {Object.entries(groupedUserVars).map(([source, vars]) => (
                            <div key={source} className="space-y-1">
                                <div className="px-1 text-[10px] font-bold text-teal-600 dark:text-teal-400 uppercase tracking-wider truncate border-b border-slate-100 dark:border-slate-800 pb-1 mb-1">
                                    {source}
                                </div>
                                {(vars as VariableDefinition[]).map((v, idx) => (
                                    <div 
                                        key={idx} 
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, v.name)}
                                        className="p-2 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg group hover:border-teal-500 transition-colors cursor-grab active:cursor-grabbing shadow-sm"
                                    >
                                        <div className="flex items-center justify-between mb-1">
                                            <div className="flex items-center gap-1.5 overflow-hidden">
                                                <GripVertical size={10} className="text-slate-300 group-hover:text-teal-500"/>
                                                <Database size={10} className="text-teal-500"/>
                                                <span className="text-xs font-mono font-bold text-slate-700 dark:text-slate-300 truncate" title={v.name}>{v.name}</span>
                                            </div>
                                            <span className="text-[9px] px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-slate-500 uppercase shrink-0 font-medium">{v.type}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}

                        {/* System Variables */}
                        {systemVars.length > 0 && (
                            <div className="space-y-1 pt-2 border-t border-slate-200 dark:border-slate-800">
                                <div className="px-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider truncate">
                                    System
                                </div>
                                {systemVars.map((v, idx) => (
                                    <div 
                                        key={idx} 
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, v.name)}
                                        className="p-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-800 rounded-lg opacity-75 hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
                                    >
                                        <div className="flex items-center justify-between mb-1">
                                            <div className="flex items-center gap-1.5 overflow-hidden">
                                                <Box size={10} className="text-slate-400"/>
                                                <span className="text-xs font-mono font-bold text-slate-600 dark:text-slate-400 truncate" title={v.name}>{v.name}</span>
                                            </div>
                                            <span className="text-[9px] px-1.5 py-0.5 bg-white dark:bg-slate-950 rounded text-slate-400 uppercase shrink-0 font-medium border border-slate-100 dark:border-slate-800">{v.type}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
